import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles } from '@material-ui/core'

// Max height for the audit tips scroll area. Viewport-relative so it scales
// gracefully across monitor sizes while still leaving room for the parameter
// controls below.
const AUDIT_TIPS_MAX_HEIGHT = '32vh'

const PANEL_BG_COLOR = '#F5F7FB'
const PANEL_TITLE_COLOR = '#185E8E'
const SECTION_TEXT_COLOR = '#1F3A5F'
const ACCENT_COLOR = '#1899D4'
const DIVIDER_COLOR = '#E5E7EB'
const SELECT_BORDER_COLOR = '#C5CDD8'
const SELECT_BORDER_HOVER_COLOR = '#9AA8B8'
const SELECT_BG_COLOR = '#FFFFFF'
const TOGGLE_ACTIVE_COLOR = '#30659F'
// Knob shadow from Toggles.svg filter (three stacked drop-shadows)
const TOGGLE_THUMB_SHADOW =
    '0px 0.57px 0.96px rgba(165,165,165,0.09), 0px 2.5px 2px rgba(165,165,165,0.10), 0px 6.13px 3.98px rgba(165,165,165,0.11)'

export const useBestPracticeManagementPanelStyles = makeStyles((theme) => ({
    sidebar: {
        paddingTop: pxToRem(20),
        background: PANEL_BG_COLOR,
        height: '100%',
        width: 0,
        display: 'none',
        transition:
            'width 0.1s cubic-bezier(0.4, 0, 1, 1), opacity 0.3s ease-in-out',
        opacity: 0,
        visibility: 'hidden',
    },
    sidebarOpen: {
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
        opacity: 1,
        visibility: 'visible',
        display: 'block',
    },
    sidebarClosed: {
        width: 0,
        opacity: 0,
        visibility: 'hidden',
        display: 'none',
    },
    panelContainer: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    titleBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingRight: 0,
    },
    title: {
        fontWeight: 600,
        color: PANEL_TITLE_COLOR,
        paddingLeft: theme.spacing(0.5),
        paddingTop: theme.spacing(0.5),
        fontSize: pxToRem(14),
    },
    closeButton: {
        paddingLeft: theme.spacing(0.5),
        color: PANEL_TITLE_COLOR,
        fontSize: pxToRem(14),
    },
    body: {
        flex: 1,
        overflowY: 'auto',
        padding: 0,
    },
    contentRoot: {
        padding: theme.spacing(1, 2),
    },
    sectionTitle: {
        fontSize: '1.3rem',
        fontWeight: 600,
        color: SECTION_TEXT_COLOR,
    },
    parameterRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(0.4, 0),
        marginLeft: 0,
        // Reserve enough height for a dropdown so toggling one on doesn't
        // shift the rows below.
        minHeight: theme.spacing(4.25),
        '& .MuiTypography-body1': {
            fontSize: '1.2rem',
        },
        '& .MuiSwitch-root': {
            marginLeft: 'auto',
        },
        '& .MuiSwitch-thumb': {
            boxShadow: TOGGLE_THUMB_SHADOW,
        },
        '& .MuiSwitch-switchBase': {
            color: '#ffffff',
        },
        '& .MuiSwitch-track': {
            backgroundColor: '#B0BEC5',
            opacity: 1,
        },
        '& .MuiSwitch-switchBase.Mui-checked': {
            color: '#ffffff',
        },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: TOGGLE_ACTIVE_COLOR,
            opacity: 1,
        },
    },
    parameterLabel: {
        fontSize: '1.2rem',
        color: SECTION_TEXT_COLOR,
        fontWeight: 600,
        flexShrink: 0,
        whiteSpace: 'nowrap',
    },
    select: {
        fontSize: '1.2rem',
        flex: 1,
        minWidth: 0,
        backgroundColor: SELECT_BG_COLOR,
        borderRadius: 4,
        '& .MuiSelect-select': {
            padding: `${pxToRem(6)} ${pxToRem(28)} ${pxToRem(6)} ${pxToRem(10)}`,
            fontSize: '1.1rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        // TextField renders a plain <input>, not <select> - it needs its own
        // padding override to match the Select rows' compact height.
        '& .MuiOutlinedInput-input': {
            padding: `${pxToRem(6)} ${pxToRem(10)}`,
            fontSize: '1.1rem',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: SELECT_BORDER_COLOR,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: SELECT_BORDER_HOVER_COLOR,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: ACCENT_COLOR,
            borderWidth: 1,
        },
    },
    divider: {
        height: pxToRem(1),
        backgroundColor: DIVIDER_COLOR,
        margin: theme.spacing(0.5, 0),
    },
    headerText: {
        fontSize: '1.2rem',
        color: SECTION_TEXT_COLOR,
        lineHeight: '1.4',
        marginBottom: theme.spacing(1),
    },
    // Constrains the audit tips list so it doesn't push the parameter
    // controls below the fold. Loosely coupled: the tips component itself
    // stays layout-agnostic; tweak this single rule when the design lands.
    auditTipsScrollContainer: {
        maxHeight: AUDIT_TIPS_MAX_HEIGHT,
        overflowY: 'auto',
    },
    rowMarginLeftZero: {
        marginLeft: 0,
    },
}))
